import { getUserRoute } from 'helpers/routes'
import { ItemWrapper } from '../../../../common/components/ItemWrapper'
import { getUserImage } from 'helpers/getUserImage'
import {
  useRelationship,
  UserAvatar,
  getDisplayUsername,
} from '@wellesley/user'
import useFollow from 'hooks/useFollow'
import FollowButton from 'components/FollowControl/FollowButton'
import { Group, UserFull } from '@wellesley/openapi'
import {
  useGetApiV1GroupsByGroupIdQuery,
  useGroup,
  getGroupChannelPath,
} from '@wellesley/group'
import pluralize from 'helpers/pluralize'
import { selectUploadFile } from '@wellesley/uploads'
import { getUserNameWithDomain } from 'helpers/getUserNameWithDomain'

type ProfileResultProps = {
  profile: UserFull
  onItemClick: (item: unknown) => void
  searchQuery?: string
}

const formatSubscriberCount = (count?: number) => {
  const safeCount = Number.isFinite(count) ? (count as number) : 0
  return new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(safeCount)
}

const ChannelAvatar = ({
  profile,
  group,
}: {
  profile: UserFull
  group: Group
}) => {
  const groupImage =
    selectUploadFile({
      size: 24,
      upload: group?.avatar,
      allowWithoutSize: true,
    })?.uri || ''

  return (
    <div data-testid="channel-avatar" className="relative flex-shrink-0">
      <UserAvatar
        name={profile.displayName}
        imgUrl={getUserImage({
          user: profile,
          type: 'avatar',
        })}
      />

      {group && (
        <div
          className="absolute -bottom-2 -right-2"
          data-testid="group-avatar-overlay"
        >
          <UserAvatar
            name={group.displayName || 'G'}
            imgUrl={groupImage}
            size={24}
            className="size-6 rounded-full border-2 border-white"
          />
        </div>
      )}
    </div>
  )
}

export const ProfileResult = ({
  profile,
  onItemClick,
  searchQuery,
}: ProfileResultProps) => {
  const isChannel = !!profile.channel
  const title = profile.displayName || profile.username

  const { data: groupData } = useGetApiV1GroupsByGroupIdQuery(
    { groupId: profile.groupId || '' },
    { skip: !isChannel || !profile.groupId },
  )

  const { group } = useGroup({
    groupname: groupData?.groupName || '',
  })

  const { follow, unfollow, renderGuestFollowModal } = useFollow({
    user: profile,
  })

  const { relationship, isLoading } = useRelationship({
    userId: profile.id,
  })

  const url =
    isChannel && profile.groupUserName && group?.groupName
      ? getGroupChannelPath({
          groupName: group.groupName,
          channel: profile,
        })
      : getUserRoute(profile)

  const handleClick = () => {
    onItemClick(profile)
  }

  const subtitle = isChannel ? (
    <span className="flex w-full items-center gap-xxxs truncate text-left text-sm font-medium text-gray-500">
      <span>Group channel</span>
      <span>
        · {formatSubscriberCount(profile.stats?.followers)}{' '}
        {pluralize('subscriber', profile.stats?.followers || 0)}
      </span>
    </span>
  ) : (
    <span className="w-full truncate text-left text-sm font-medium text-gray-500">
      {getDisplayUsername(getUserNameWithDomain(profile))}
    </span>
  )

  const avatar =
    isChannel && group ? (
      <ChannelAvatar profile={profile} group={group} />
    ) : (
      <UserAvatar
        name={profile.displayName}
        imgUrl={getUserImage({
          user: profile,
          type: 'avatar',
        })}
      />
    )

  const followButtonText = isChannel ? 'Subscribe' : 'Follow'
  const unfollowButtonText = isChannel ? 'Unsubscribe' : 'Unfollow'

  return (
    <ItemWrapper url={url} onClick={handleClick} searchQuery={searchQuery}>
      {avatar}

      <div className="flex min-w-0 flex-1 flex-col items-start">
        <span className="w-full truncate text-left text-base font-semibold text-gray-900">
          {title}
        </span>
        {subtitle}
      </div>

      <div
        className="ml-auto"
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
      >
        <FollowButton
          user={profile}
          follow={() => void follow()}
          unfollow={() => void unfollow()}
          relationship={relationship}
          followButtonText={followButtonText}
          unfollowButtonText={unfollowButtonText}
          isRelationshipPending={isLoading}
          size="s"
          className="min-w-[80px]"
        />
        {renderGuestFollowModal()}
      </div>
    </ItemWrapper>
  )
}
